import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

/**
 * A part of a whole, drawn as a ring.
 *
 * ## WHY A RING AND NOT A BAR
 *
 * `ThresholdBar` on the Overview already draws a rate against a line it is being
 * measured against, in `good`/`warn`/`bad`. That answers *is this bad?* This
 * answers a different question — *how much of the whole is this?* — and it is
 * the question the figures on a workspace with no delivery reports can actually
 * support. A ring reads as a share at a glance where a bare `335` does not.
 *
 * ## THE TONE IS THE CALLER'S, AND THAT IS DELIBERATE
 *
 * This is a `packages/ui` primitive, so it takes any tone, exactly as `Badge`
 * does. It does **not** know whether the number it is drawing was measured.
 *
 * That knowledge lives where it always lives in this codebase: in the component
 * that holds the contract shape. `ProjectedFigureRow` takes a `ProjectedFigures`
 * and nothing else, so everything it draws is a projection, and it passes
 * `info` — never `good`, which would put a verdict on an assumed number and is
 * the exact shape of the two criticals this project has shipped.
 * `verify-dashboard` asserts no verdict tone reaches a projected ring.
 *
 * ## ACCESSIBILITY, AND THE ONE THING THAT BIT THIS PACKAGE BEFORE
 *
 * The `<svg>` is `aria-hidden` and every number inside it is also present as
 * text, which is `TimeSeriesChart`'s rule and the reason a screen reader gets
 * figures rather than a shrug.
 *
 * **Forced-colours mode replaces every fill with the system palette**, and
 * `ThresholdBar` shipped invisible there once: its track and its fill were both
 * backgrounds, so both became Canvas and the bar vanished. Review caught it. The
 * same trap applies here, so the track is drawn with an explicit
 * `forced-colors:stroke-[CanvasText]` and the value arc takes `Highlight` — each
 * of which survives the substitution, and they differ from one another.
 */
export type ProportionTone = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

const TONE_TEXT: Readonly<Record<ProportionTone, string>> = {
  good: 'text-good',
  warn: 'text-warn',
  bad: 'text-bad',
  info: 'text-info',
  neutral: 'text-muted',
};

/** Radius and circumference of the arc, in the viewBox's own units. */
const RADIUS = 15.5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface ProportionRingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The part. Clamped into `[0, total]` so a bad input cannot draw past the ring. */
  readonly value: number;
  /** The whole. A total of zero draws an empty ring rather than dividing by it. */
  readonly total: number;
  readonly tone: ProportionTone;
  /** What the figure is, under the ring. */
  readonly label: string;
  /**
   * The number in the middle, already formatted by the caller.
   *
   * Formatting is the screen's — `formatCount` and `formatRate` live in the web
   * app, and a design-system primitive that knew about thousands separators
   * would have to know about locales next.
   */
  readonly centre: string;
  /** The count beside the label, already formatted. Omitted where there is none. */
  readonly detail?: string;
}

export function ProportionRing({
  value,
  total,
  tone,
  label,
  centre,
  detail,
  className,
  ...rest
}: ProportionRingProps) {
  const share = total <= 0 ? 0 : Math.min(1, Math.max(0, value / total));
  const drawn = CIRCUMFERENCE * share;

  return (
    <div className={cn('flex flex-col items-center gap-2', className)} {...rest}>
      <div className="relative size-[4.5rem]">
        <svg viewBox="0 0 36 36" aria-hidden="true" className="size-full -rotate-90">
          <circle
            cx="18"
            cy="18"
            r={RADIUS}
            fill="none"
            strokeWidth="3"
            className="stroke-white/[0.08] forced-colors:stroke-[CanvasText]"
          />
          {/* `currentColor` under the tone class, so one class decides the
              colour and forced-colours can override it wholesale. */}
          <g className={TONE_TEXT[tone]}>
            <circle
              cx="18"
              cy="18"
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${drawn.toFixed(3)} ${(CIRCUMFERENCE - drawn).toFixed(3)}`}
              className="forced-colors:stroke-[Highlight]"
            />
          </g>
        </svg>

        {/* The figure, as real text rather than `<text>`: selectable, and it
            inherits the page's font rather than the SVG's. */}
        <span className="absolute inset-0 flex items-center justify-center font-mono text-[0.95rem] tabular-nums text-text">
          {centre}
        </span>
      </div>

      <div className="flex flex-col items-center gap-0.5">
        <span className="font-mono text-[0.62rem] uppercase tracking-[0.12em] text-muted">
          {label}
        </span>
        {detail !== undefined && (
          <span className="font-mono text-[0.72rem] tabular-nums text-muted">{detail}</span>
        )}
      </div>
    </div>
  );
}

/**
 * The same part-of-a-whole, drawn as a bar, for a row of them.
 *
 * A ring costs about 4.5rem of height and reads well for two or three figures
 * side by side. Past that it becomes a wall of circles, and a row of bars is
 * both denser and easier to compare along a shared left edge — which is what the
 * breakdown strip underneath a chart is for.
 *
 * Same rules as the ring: `aria-hidden`, the number beside it in text, and a
 * track that survives forced colours.
 */
export interface ProportionBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  readonly value: number;
  readonly total: number;
  readonly tone: ProportionTone;
}

const TONE_BAR: Readonly<Record<ProportionTone, string>> = {
  good: 'bg-good',
  warn: 'bg-warn',
  bad: 'bg-bad',
  info: 'bg-info',
  neutral: 'bg-muted',
};

export function ProportionBar({ value, total, tone, className, ...rest }: ProportionBarProps) {
  const share = total <= 0 ? 0 : Math.min(1, Math.max(0, value / total));

  return (
    <div
      aria-hidden="true"
      className={cn(
        'h-1.5 w-full rounded-full bg-white/[0.08] forced-colors:border forced-colors:border-[CanvasText]',
        className,
      )}
      {...rest}
    >
      <div
        className={cn('h-full rounded-full forced-colors:bg-[Highlight]', TONE_BAR[tone])}
        style={{ width: `${(share * 100).toFixed(2)}%` }}
      />
    </div>
  );
}
